import { store } from 'appState/store'
import axios from 'axios'
import Keycloak from 'keycloak-js'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { notificator } from 'utils/notificator/notificator'
import storage, { StorageEnum } from 'utils/storage'
import { setAuthUser } from './authSlice'
import { env } from 'configs/env'

const keycloakInstance = new (Keycloak as any)({
  url: 'https://auth.phoenix.ops.virtualoutbound.com/',
  realm: env.VITE_KEYCLOAK_CLIENT_REALM,
  clientId: 'aquarius-ui',
})

export const parseLoginResponseToStateCurrentUser = (token: string) => {
  const decodedToken = JSON.parse(atob(token.split('.')[1]))
  const currentUser = {
    id: decodedToken.sub,
    username: decodedToken.preferred_username,
    email: decodedToken.email,
    email_verified: decodedToken.email_verified,
    family_name: decodedToken.family_name,
    given_name: decodedToken.given_name,
    phone_number: decodedToken.phone_number,
    phone_number_verified: decodedToken.phone_number_verified,
  }
  localStorage.setItem('currentUser', JSON.stringify(currentUser))
  storage.set(StorageEnum.currentUser, currentUser)
  return currentUser
}

const setupTokenRefresh = () => {
  const tokenExp = keycloakInstance.tokenParsed?.exp

  if (!tokenExp) {
    return
  }

  const expirationTime = tokenExp * 1000 // Convert to milliseconds
  const now = Date.now()
  const refreshTime = expirationTime - now - 500000 // Refresh 5 minutes before expiration
  if (refreshTime <= 0) {
    keycloakRefresh().catch((err) => {
      console.error('Immediate token refresh failed:', err)
      signOut('true')
    })
    return
  }

  setTimeout(() => {
    keycloakRefresh()
      .then(() => {
        // Recursively schedule next refresh
        setupTokenRefresh()
      })
      .catch((error) => {
        console.error('Scheduled token refresh failed, logging out', error)
        signOut('true')
      })
  }, refreshTime)
}

const login = async () => {
  try {
    const authenticated = await keycloakInstance.init({
      onLoad: 'login-required',
      checkLoginIframe: false,
    })

    if (!authenticated) {
      console.warn('User is not authenticated')
      return null
    }

    const token = keycloakInstance.token
    const currentUser = parseLoginResponseToStateCurrentUser(token as string)
    store.dispatch(setAuthUser(currentUser))

    // Store tokens
    localStorage.setItem('token', token as string)
    localStorage.setItem('refreshToken', keycloakInstance.refreshToken as string)
    localStorage.setItem('idToken', keycloakInstance.idToken as string)
    localStorage.setItem('tokenParsed', JSON.stringify(keycloakInstance.tokenParsed))

    setupTokenRefresh()
    return currentUser
  } catch (error) {
    console.error('Login failed:', error)
    throw error
  }
}

const initKeycloak = async (onAuthenticatedCallback: (token: string | null) => void) => {
  const token = localStorage.getItem('token')
  const refreshToken = localStorage.getItem('refreshToken')
  const idToken = localStorage.getItem('idToken')

  try {
    const authenticated = await keycloakInstance.init({
      onLoad: 'login-required',
      checkLoginIframe: false,
      token,
      refreshToken,
      idToken,
    })

    if (authenticated) {
      const token = keycloakInstance.token
      const currentUser = parseLoginResponseToStateCurrentUser(token as string)
      store.dispatch(setAuthUser(currentUser))

      localStorage.setItem('token', token as string)
      localStorage.setItem('refreshToken', keycloakInstance.refreshToken as string)
      localStorage.setItem('idToken', keycloakInstance.idToken as string)
      localStorage.setItem('tokenParsed', JSON.stringify(keycloakInstance.tokenParsed))

      setupTokenRefresh()
      onAuthenticatedCallback(token || null)
    } else {
      console.warn('User is not authenticated')
      onAuthenticatedCallback(null)
    }
  } catch (error) {
    console.error('Keycloak initialization failed:', error)
    onAuthenticatedCallback(null)
  }
}

const getAuthToken = () => {
  keycloakInstance.token != undefined ? parseLoginResponseToStateCurrentUser(keycloakInstance.token as string) : null
  return keycloakInstance.token
}

const signOut = (session = 'false') => {
  const payload = {
    redirector: window.location.origin,
    status : session
  }
  keycloakInstance
    .logout({
      redirectUri: `${keycloakInstance.authServerUrl}/realms/${keycloakInstance.realm}/protocol/openid-connect/logout?sessionExpired=${encodeURIComponent(JSON.stringify(payload))}`,
    })
    .then(() => {
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('idToken')
      localStorage.removeItem('tokenParsed')
      localStorage.removeItem('currentUser')
      storage.remove(StorageEnum.currentUser)
      localStorage.clear()
    })
    .catch((err: any) => {
      console.error('Logout error:', err)
      notificator.notify({ type: 'error', heading: 'Logout failed' })
    })
}

const keycloakRefresh = async () => {
  try {
    await keycloakInstance.updateToken(30)
    return keycloakInstance.token
  } catch (error) {
    console.error('Error refreshing token:', error)
    throw error
  }
}

const isAuthenticated = () => {
  return keycloakInstance.authenticated
}

const handleChangePassword = async (oldPassword: string, newPassword: string) => {
  try {
    const url = `${keycloakInstance.authServerUrl}/realms/${keycloakInstance.realm}/account/password`
    const headers = {
      Authorization: `Bearer ${keycloakInstance.token}`,
      'Content-Type': 'application/json',
    }
    const body = {
      currentPassword: oldPassword,
      newPassword: newPassword,
    }

    await axios.put(url, body, { headers })
    notificator.notify({ type: 'success', heading: 'Password changed successfully' })
  } catch (error: any) {
    notificator.notify({ type: 'error', heading: error.message || ErrorMessagesEnum.SomethingWentWrong })
  }
}

export const authApi = {
  login,
  initKeycloak,
  signOut,
  getAuthToken,
  keycloakRefresh,
  isAuthenticated,
  handleChangePassword,
}
